import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    UserPlus, ClipboardList, Upload, Activity, AlertTriangle,
    ClipboardCheck, CheckCircle2, ArrowUpRight, ArrowDownRight,
    TrendingUp, Users, Clock, Shield, Bot, CheckCircle,
    AlertCircle, FileText, MessageSquare, Loader2, RefreshCw,
    ChevronRight, BarChart3, Briefcase, UserCheck, BookOpen,
    Zap, ShieldCheck, ShieldAlert, Eye
} from 'lucide-react';
import api from '../utils/api';

// ═══════════════════════════════════════════════════════════
//  DESIGN TOKENS
// ═══════════════════════════════════════════════════════════
const T = {
    textPrimary: 'var(--foreground)',
    textSecondary: 'var(--muted-foreground)',
    textMuted: 'var(--muted-foreground)',
    accent: 'var(--accent)',
    accentHover: 'var(--accent-light)',
    accentBg: '#FEF2EE',
    border: 'var(--border)',
    borderLight: 'var(--border)',
    bgPage: 'var(--background)',
    bgCard: 'var(--card)',
    bgHover: 'var(--secondary)',
    warning: 'var(--warning)',
    warningBg: '#FEF3C7',
    warningText: '#92400E',
    success: '#10b981',
    successBg: '#ECFDF5',
    successText: '#065f46',
    danger: '#ef4444',
    dangerBg: '#FEF2F2',
    dangerText: '#991b1b',
    info: '#3b82f6',
    infoBg: '#EFF6FF',
    infoText: '#1e40af',
    purple: '#8b5cf6',
    purpleBg: '#F5F3FF',
    purpleText: '#6d28d9',
    cardBorder: '1px solid var(--border)',
    cardRadius: '8px',
    cardPadding: '20px',
    cardShadow: '0 1px 3px rgba(0,0,0,0.04)',
    cardShadowHover: '0 4px 16px rgba(0,0,0,0.08)',
    fontSm: '13px',
    fontBase: '14px',
    fontLg: '16px',
    fontXl: '20px',
    fontXxl: '24px',
    btnRadius: '8px',
    btnPadding: '8px 16px',
};

// ── Skeleton ──
const Skeleton = ({ width = '100%', height = '20px', borderRadius = T.cardRadius }) => (
    <div style={{
        width, height, borderRadius,
        background: `linear-gradient(90deg, ${T.bgHover} 25%, ${T.bgCard} 50%, ${T.bgHover} 75%)`,
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
    }} />
);

// ── Quick Action ──
const QuickAction = ({ icon: Icon, label, onClick, primary }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                height: '36px', padding: T.btnPadding, fontSize: T.fontBase,
                fontWeight: 600, borderRadius: T.btnRadius, cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: primary ? 'none' : T.cardBorder,
                background: primary ? (hovered ? T.accentHover : T.accent) : (hovered ? T.bgHover : T.bgCard),
                color: primary ? '#FFF' : T.textPrimary,
                boxShadow: primary ? '0 2px 8px rgba(232, 111, 74, 0.2)' : '0 1px 2px rgba(0,0,0,0.03)',
            }}
        >
            <Icon size={16} />
            {label}
        </button>
    );
};

// ── Lifecycle Card ──
const LifecycleCard = ({ value, label, sublabel, icon: Icon, color, bgColor, onClick, pulse }) => {
    const [hov, setHov] = useState(false);
    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                background: T.bgCard, borderTop: T.cardBorder, borderRight: T.cardBorder, borderBottom: T.cardBorder, borderRadius: T.cardRadius,
                padding: T.cardPadding, cursor: 'pointer', transition: 'all 0.25s ease',
                boxShadow: hov ? T.cardShadowHover : T.cardShadow,
                borderLeft: `4px solid ${color}`,
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                minHeight: '130px', position: 'relative',
                transform: hov ? 'translateY(-2px)' : 'none',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{
                    width: '42px', height: '42px', borderRadius: '10px',
                    background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Icon size={20} color={color} />
                </div>
                {pulse && (
                    <span style={{
                        width: '8px', height: '8px', borderRadius: '50%', background: color,
                        animation: 'pulse 2s infinite'
                    }} />
                )}
            </div>
            <div style={{ marginTop: '14px' }}>
                <div style={{
                    fontSize: '30px', fontWeight: 700, color: T.textPrimary, lineHeight: 1.1,
                    fontVariantNumeric: 'tabular-nums'
                }}>
                    {value}
                </div>
                <div style={{ fontSize: T.fontSm, color: T.textSecondary, fontWeight: 500, marginTop: '4px' }}>
                    {label}
                </div>
                {sublabel && (
                    <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '2px' }}>{sublabel}</div>
                )}
            </div>
        </div>
    );
};

// ── Metric Row ──
const MetricRow = ({ icon, label, value, detail, change, changeDir }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: '14px',
        padding: '14px 0', borderBottom: `1px solid ${T.borderLight}`,
    }}>
        <div style={{
            fontSize: '20px', width: '36px', height: '36px',
            background: T.bgHover, borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
            {icon}
        </div>
        <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: T.fontBase, color: T.textSecondary, fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: T.fontBase, color: T.textPrimary, fontWeight: 700 }}>{value}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                <span style={{ fontSize: '12px', color: T.textMuted }}>{detail}</span>
                {change !== null && change !== undefined && (
                    <span style={{
                        fontSize: '12px', fontWeight: 600,
                        color: changeDir === 'up' ? T.successText : changeDir === 'down' ? T.dangerText : T.textMuted,
                        display: 'flex', alignItems: 'center', gap: '2px',
                        background: changeDir === 'up' ? T.successBg : changeDir === 'down' ? T.dangerBg : T.bgHover,
                        padding: '1px 6px', borderRadius: '4px'
                    }}>
                        {changeDir === 'up' ? <ArrowUpRight size={10} /> : changeDir === 'down' ? <ArrowDownRight size={10} /> : null}
                        {change > 0 ? `+${change}` : change}{typeof change === 'number' ? '%' : ''}
                    </span>
                )}
            </div>
        </div>
    </div>
);

// ── Activity Item ──
const ActivityItem = ({ icon: Icon, iconColor, iconBg, title, subtitle, time }) => (
    <div style={{
        display: 'flex', alignItems: 'start', gap: '12px', padding: '10px 0',
        borderBottom: `1px solid ${T.borderLight}`,
    }}>
        <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: iconBg, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0, marginTop: '2px'
        }}>
            <Icon size={14} color={iconColor} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: T.fontBase, color: T.textPrimary, fontWeight: 500, lineHeight: '1.4' }}>
                {title}
            </div>
            <div style={{ fontSize: '12px', color: T.textSecondary, marginTop: '2px' }}>
                {subtitle}
            </div>
        </div>
        <div style={{ fontSize: '11px', color: T.textMuted, whiteSpace: 'nowrap', marginTop: '4px' }}>
            {time}
        </div>
    </div>
);

// ── Compliance Widget ──
const ComplianceWidget = ({ rate, violations, resolved, onClick }) => {
    const pct = rate ?? 0;
    const statusColor = pct >= 90 ? T.success : pct >= 70 ? T.warningText : T.danger;
    const statusBg = pct >= 90 ? T.successBg : pct >= 70 ? T.warningBg : T.dangerBg;
    const statusLabel = pct >= 90 ? 'Healthy' : pct >= 70 ? 'At Risk' : 'Critical';
    const StatusIcon = pct >= 90 ? ShieldCheck : ShieldAlert;

    // SVG ring
    const size = 80;
    const stroke = 6;
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ * (1 - pct / 100);

    return (
        <div
            onClick={onClick}
            style={{
                background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius,
                padding: T.cardPadding, boxShadow: T.cardShadow, cursor: 'pointer',
                transition: 'all 0.2s',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Shield size={18} color={statusColor} />
                <h3 style={{ fontSize: T.fontLg, fontWeight: 700, margin: 0 }}>Compliance</h3>
                <span style={{
                    marginLeft: 'auto', fontSize: '12px', fontWeight: 600,
                    color: statusColor, background: statusBg,
                    padding: '2px 10px', borderRadius: '12px',
                    display: 'flex', alignItems: 'center', gap: '4px'
                }}>
                    <StatusIcon size={12} />
                    {statusLabel}
                </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                {/* Ring */}
                <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
                    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                            stroke={T.bgHover} strokeWidth={stroke} />
                        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                            stroke={statusColor} strokeWidth={stroke}
                            strokeDasharray={circ} strokeDashoffset={offset}
                            strokeLinecap="round"
                            style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
                    </svg>
                    <div style={{
                        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center'
                    }}>
                        <span style={{ fontSize: '18px', fontWeight: 700, color: T.textPrimary }}>{pct}%</span>
                    </div>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: T.fontSm, color: T.textSecondary }}>Open violations</span>
                        <span style={{
                            fontSize: T.fontSm, fontWeight: 700,
                            color: (violations ?? 0) > 0 ? T.dangerText : T.successText
                        }}>
                            {violations ?? 0}
                        </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: T.fontSm, color: T.textSecondary }}>Resolved (30d)</span>
                        <span style={{ fontSize: T.fontSm, fontWeight: 700, color: T.successText }}>
                            {resolved ?? 0}
                        </span>
                    </div>
                    {(violations ?? 0) > 0 && (
                        <div style={{
                            fontSize: '12px', color: T.accent, fontWeight: 600, marginTop: '4px',
                            display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'
                        }}>
                            View details <ChevronRight size={12} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Insight Card ──
const InsightCard = ({ icon, message, action, onClick }) => {
    const [hov, setHov] = useState(false);
    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '14px 16px',
                background: hov ? '#F1F5F9' : '#F8FAFC',
                border: `1px solid ${hov ? T.accent : T.borderLight}`,
                borderRadius: T.cardRadius, cursor: 'pointer',
                transition: 'all 0.2s', marginBottom: '8px'
            }}
        >
            <div style={{ marginTop: '2px', fontSize: '16px' }}>{icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: T.fontBase, color: T.textPrimary, fontWeight: 500, lineHeight: '1.4' }}>
                    {message}
                </div>
                {action && (
                    <div style={{
                        fontSize: '12px', color: T.accent, fontWeight: 600, marginTop: '6px',
                        display: 'flex', alignItems: 'center', gap: '4px'
                    }}>
                        {action} <ChevronRight size={12} />
                    </div>
                )}
            </div>
        </div>
    );
};


// ═══════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const DashboardHomeAdmin = ({ sidebarCollapsed, onMenuClick, onAddEmployee }) => {
    const [admin, setAdmin] = useState(null);
    const [homeData, setHomeData] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingActivities, setLoadingActivities] = useState(true);
    const navigate = useNavigate();

    const fetchAdminInfo = useCallback(async () => {
        try {
            const response = await api.get('/auth/me');
            setAdmin(response.data.user);
        } catch (error) {
            console.error('Error fetching admin:', error);
        }
    }, []);

    const fetchHomeData = useCallback(async () => {
        try {
            const response = await api.get('/dashboard/admin/home');
            setHomeData(response.data.home);
        } catch (error) {
            console.error('Error fetching home data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRecentActivity = useCallback(async () => {
        try {
            const response = await api.get('/admin/notifications/recent-activity', {
                params: { limit: 8 }
            });
            setActivities(response.data.activities || []);
        } catch (error) {
            console.error('Error fetching recent activity:', error);
        } finally {
            setLoadingActivities(false);
        }
    }, []);

    useEffect(() => {
        fetchAdminInfo();
        fetchHomeData();
        fetchRecentActivity();
        const interval = setInterval(() => { fetchHomeData(); fetchRecentActivity(); }, 60000);
        return () => clearInterval(interval);
    }, [fetchAdminInfo, fetchHomeData, fetchRecentActivity]);

    const checkPerm = (perm) => {
        if (!admin || !admin.permissions) return true;
        const scope = admin.permissions[perm];
        return scope && scope !== 'none' && scope !== false;
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'task_completed':
                return { icon: CheckCircle2, color: T.successText, bg: T.successBg };
            case 'employee_added':
                return { icon: UserPlus, color: T.infoText, bg: T.infoBg };
            case 'task_assigned':
                return { icon: FileText, color: T.purpleText, bg: T.purpleBg };
            case 'task_overdue':
                return { icon: AlertCircle, color: T.dangerText, bg: T.dangerBg };
            case 'survey_completed':
                return { icon: ClipboardList, color: T.accent, bg: T.accentBg };
            case 'survey_assigned':
                return { icon: ClipboardList, color: T.purpleText, bg: T.purpleBg };
            case 'message_received':
                return { icon: MessageSquare, color: T.accent, bg: T.accentBg };
            default:
                return { icon: Clock, color: T.textSecondary, bg: T.bgHover };
        }
    };

    const formatActivityTime = (date) => {
        if (!date) return '';
        const now = new Date();
        const d = new Date(date);
        const diffMs = now - d;
        const mins = Math.floor(diffMs / 60000);
        const hrs = Math.floor(diffMs / 3600000);
        const days = Math.floor(diffMs / 86400000);
        if (mins < 1) return 'Just now';
        if (mins < 60) return `${mins}m ago`;
        if (hrs < 24) return `${hrs}h ago`;
        if (days === 0) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days}d ago`;
        return d.toLocaleDateString();
    };

    const activityItems = activities.map(a => {
        const ic = getActivityIcon(a.activity_type);
        return { icon: ic.icon, iconColor: ic.color, iconBg: ic.bg, title: a.title, subtitle: a.subtitle || '', time: formatActivityTime(a.activity_time) };
    });
    const displayActivities = activityItems.length > 0 ? activityItems : [
        { icon: Clock, iconColor: T.textSecondary, iconBg: T.bgHover, title: 'No recent activity', subtitle: 'Activity will appear here', time: '' }
    ];

    const d = homeData;

    return (
        <>
            <style>{`
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
            `}</style>

            <main style={{ padding: '32px', maxWidth: '1280px', margin: '0 auto', color: T.textPrimary }}>

                {/* ── HEADER ── */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
                    <div>
                        <h1 style={{ fontSize: T.fontXxl, fontWeight: 700, color: T.textPrimary, margin: 0 }}>
                            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {admin?.company_name || 'Admin'}
                        </h1>
                        <p style={{ fontSize: T.fontBase, color: T.textSecondary, marginTop: '4px' }}>
                            Here's what's happening in your organization today.
                        </p>
                    </div>
                    <button
                        onClick={() => { setLoading(true); setLoadingActivities(true); fetchHomeData(); fetchRecentActivity(); }}
                        style={{
                            background: T.bgCard, border: T.cardBorder, borderRadius: T.btnRadius,
                            padding: '8px', cursor: 'pointer', color: T.textSecondary,
                            boxShadow: T.cardShadow, transition: 'all 0.2s'
                        }}
                        title="Refresh data"
                    >
                        <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* ── QUICK ACTIONS ── */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
                    {checkPerm('employee.add') && (
                        <QuickAction icon={UserPlus} label="Add Person" primary onClick={onAddEmployee} />
                    )}
                    {checkPerm('task.create') && (
                        <QuickAction icon={ClipboardList} label="Create Task" onClick={() => navigate('/admin/tasks')} />
                    )}
                    {checkPerm('file.upload') && (
                        <QuickAction icon={Upload} label="Upload File" onClick={() => navigate('/admin/files')} />
                    )}
                    <QuickAction icon={BarChart3} label="Analytics" onClick={() => navigate('/admin/activity-centre', { state: { tab: 'analytics' } })} />
                </div>

                {/* ── SECTION 1: LIFECYCLE COUNTS (3 separate) + AT A GLANCE (4 cards) ── */}
                <div style={{ marginBottom: '28px' }}>
                    <h2 style={{ fontSize: T.fontLg, fontWeight: 700, color: T.textPrimary, marginBottom: '14px' }}>
                        Employee Lifecycle
                    </h2>

                    {loading ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius, padding: T.cardPadding }}>
                                    <Skeleton width="40px" height="40px" borderRadius="10px" />
                                    <div style={{ marginTop: '16px' }}>
                                        <Skeleton width="40%" height="28px" />
                                        <Skeleton width="60%" height="14px" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                            <LifecycleCard
                                value={d?.keyMetrics?.activeEmployees?.preboarding ?? 0}
                                label="Preboarding"
                                sublabel="Awaiting start date"
                                icon={UserPlus}
                                color={T.info}
                                bgColor={T.infoBg}
                                pulse={(d?.keyMetrics?.activeEmployees?.preboarding ?? 0) > 0}
                                onClick={() => navigate('/admin/employees', { state: { filter: 'preboarding' } })}
                            />
                            <LifecycleCard
                                value={d?.keyMetrics?.activeEmployees?.onboarding ?? 0}
                                label="Onboarding"
                                sublabel="Actively onboarding"
                                icon={BookOpen}
                                color={T.accent}
                                bgColor={T.accentBg}
                                pulse={(d?.keyMetrics?.activeEmployees?.onboarding ?? 0) > 0}
                                onClick={() => navigate('/admin/employees', { state: { filter: 'onboarding' } })}
                            />
                            <LifecycleCard
                                value={(d?.keyMetrics?.activeEmployees?.total ?? 0) - (d?.keyMetrics?.activeEmployees?.onboarding ?? 0)}
                                label="Active"
                                sublabel="Onboarding complete"
                                icon={UserCheck}
                                color={T.success}
                                bgColor={T.successBg}
                                onClick={() => navigate('/admin/employees', { state: { filter: 'active' } })}
                            />
                        </div>
                    )}
                </div>

                {/* ── SECTION 2: AT A GLANCE ── */}
                <div style={{ marginBottom: '28px' }}>
                    <h2 style={{ fontSize: T.fontLg, fontWeight: 700, color: T.textPrimary, marginBottom: '14px' }}>
                        At a Glance
                    </h2>

                    {loading ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={{ background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius, padding: T.cardPadding }}>
                                    <Skeleton width="40px" height="40px" borderRadius="10px" />
                                    <div style={{ marginTop: '16px' }}>
                                        <Skeleton width="40%" height="28px" />
                                        <Skeleton width="60%" height="14px" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                            <LifecycleCard
                                value={d?.atAGlance?.activeActivities ?? 0}
                                label="Active Items"
                                icon={Activity}
                                color={T.accent}
                                bgColor={T.accentBg}
                                onClick={() => navigate('/admin/activity-centre', { state: { tab: 'active' } })}
                            />
                            <LifecycleCard
                                value={d?.atAGlance?.overdueItems ?? 0}
                                label="Overdue"
                                icon={AlertTriangle}
                                color={T.danger}
                                bgColor={T.dangerBg}
                                pulse={(d?.atAGlance?.overdueItems ?? 0) > 0}
                                onClick={() => navigate('/admin/activity-centre', { state: { tab: 'overdue' } })}
                            />
                            <LifecycleCard
                                value={d?.atAGlance?.pendingReviews ?? 0}
                                label="Pending Review"
                                icon={Eye}
                                color={T.warningText}
                                bgColor={T.warningBg}
                                onClick={() => navigate('/admin/activity-centre', { state: { tab: 'pending_review' } })}
                            />
                            <LifecycleCard
                                value={d?.atAGlance?.completedRecent ?? 0}
                                label="Completed"
                                icon={CheckCircle2}
                                color={T.successText}
                                bgColor={T.successBg}
                                onClick={() => navigate('/admin/activity-centre', { state: { tab: 'completed' } })}
                            />
                        </div>
                    )}
                </div>

                {/* ── SECTION 3: METRICS + COMPLIANCE | ACTIVITY STREAM | AI INSIGHTS ── */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '20px',
                }}>
                    {/* ── Column 1: Performance + Compliance ── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Performance Metrics */}
                        <div style={{
                            background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius,
                            padding: T.cardPadding, boxShadow: T.cardShadow,
                        }}>
                            <h3 style={{ fontSize: T.fontLg, fontWeight: 700, margin: '0 0 4px 0' }}>Performance</h3>
                            {loading ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '14px' }}>
                                    {[1, 2, 3].map(i => <Skeleton key={i} height="50px" />)}
                                </div>
                            ) : (
                                <div>
                                    <MetricRow
                                        icon={<BarChart3 size={18} color={T.accent} />}
                                        label="Items Completed"
                                        value={`${d?.keyMetrics?.taskCompletion?.value ?? 0}%`}
                                        change={d?.keyMetrics?.taskCompletion?.change ?? null}
                                        changeDir={(d?.keyMetrics?.taskCompletion?.change ?? 0) >= 0 ? 'up' : 'down'}
                                    />
                                    <MetricRow
                                        icon={<Clock size={18} color={T.warningText} />}
                                        label="Onboarding Time"
                                        value={d?.keyMetrics?.avgOnboardingTime?.value != null ? `${d.keyMetrics.avgOnboardingTime.value}d` : 'N/A'}
                                        change={d?.keyMetrics?.avgOnboardingTime?.change}
                                        changeDir={d?.keyMetrics?.avgOnboardingTime?.change > 0 ? 'up' : d?.keyMetrics?.avgOnboardingTime?.change < 0 ? 'down' : null}
                                        detail="Average completion time"
                                    />
                                    <MetricRow
                                        icon={<Users size={18} color={T.infoText} />}
                                        label="Total Employees"
                                        value={
                                            (d?.keyMetrics?.activeEmployees?.total ?? 0) +
                                            (d?.keyMetrics?.activeEmployees?.preboarding ?? 0)
                                        }
                                        detail={`${d?.keyMetrics?.activeEmployees?.preboarding ?? 0} preboarding · ${d?.keyMetrics?.activeEmployees?.onboarding ?? 0} onboarding`}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Compliance Widget */}
                        {!loading && (
                            <ComplianceWidget
                                rate={d?.keyMetrics?.complianceRate?.value}
                                violations={d?.keyMetrics?.complianceRate?.violations}
                                resolved={d?.keyMetrics?.complianceRate?.resolved}
                                onClick={() => navigate('/admin/activity-centre', { state: { tab: 'compliance' } })}
                            />
                        )}
                    </div>

                    {/* ── Column 2: Activity Stream ── */}
                    <div style={{
                        background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius,
                        padding: T.cardPadding, boxShadow: T.cardShadow,
                        display: 'flex', flexDirection: 'column',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Zap size={18} color={T.accent} />
                                <h3 style={{ fontSize: T.fontLg, fontWeight: 700, margin: 0 }}>Activity Stream</h3>
                            </div>
                            {loadingActivities && <Loader2 size={16} className="animate-spin" style={{ color: T.textSecondary }} />}
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', minHeight: '320px' }}>
                            {displayActivities.map((act, i) => (
                                <ActivityItem key={i} {...act} />
                            ))}
                        </div>

                        {activities.length > 0 && (
                            <button
                                onClick={() => navigate('/admin/notifications')}
                                style={{
                                    marginTop: '14px', width: '100%', padding: '10px',
                                    background: T.bgHover, color: T.textPrimary, border: 'none',
                                    borderRadius: T.btnRadius, fontSize: T.fontSm, fontWeight: 600,
                                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '6px',
                                    transition: 'background 0.2s'
                                }}
                            >
                                View full history <ArrowUpRight size={14} />
                            </button>
                        )}
                    </div>

                    {/* ── Column 3: AI Insights ── */}
                    <div style={{
                        background: T.bgCard, border: T.cardBorder, borderRadius: T.cardRadius,
                        padding: T.cardPadding, boxShadow: T.cardShadow,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <Bot size={20} color={T.accent} />
                            <h3 style={{ fontSize: T.fontLg, fontWeight: 700, margin: 0 }}>Smart Insights</h3>
                        </div>

                        {loading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {[1, 2, 3].map(i => <Skeleton key={i} height="70px" borderRadius={T.cardRadius} />)}
                            </div>
                        ) : (d?.aiInsights?.length > 0) ? (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {d.aiInsights.map((insight, i) => (
                                    <InsightCard
                                        key={i}
                                        icon={insight.icon}
                                        message={insight.message}
                                        action={insight.action}
                                        onClick={() => navigate(insight.link)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', padding: '40px 16px', color: T.textMuted,
                                textAlign: 'center', border: `1px dashed ${T.border}`,
                                borderRadius: T.cardRadius, background: T.bgPage
                            }}>
                                <CheckCircle size={32} color={T.success} style={{ marginBottom: '12px', opacity: 0.8 }} />
                                <div style={{ fontSize: T.fontBase, fontWeight: 600, color: T.textPrimary }}>All caught up!</div>
                                <div style={{ fontSize: T.fontSm, marginTop: '4px' }}>No urgent insights right now.</div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </>
    );
};

export default DashboardHomeAdmin;
